<template>
  <div id="page-layout">
    <div id="page-header">
      <div class="page-title-layout">
        <router-link class="bc-link" :to="{name: isClient() ? 'clientList' : 'adminList'}">
          {{$t(`user.${pageMode}_list_title`)}}
        </router-link>
        <i class="fas fa-angle-right"></i>
        <p>{{$t("header.title_profile")}}</p>
      </div>
    </div>
    <div class="block-content">
      <div class="grid-title">
        <div class="column">
          <div class="big-avatar-circle">
            <span class="initials">{{Utils.getInitials(user)}}</span>
          </div>
          <div>
            <div class="display-name">{{user.displayName}}</div>
            <div class="email-blue">{{user.email}}</div>
          </div>
        </div>
        <div class="actions">
          <button class="solid-button" type="button" v-on:click="editUser(user.id)">{{$t(`actions.edit_${pageMode}`)}}
          </button>
        </div>
      </div>
      <div class="grid-data" v-if="isClient()">
        <div class="info-title">{{$t("user.company")}}</div>
        <div class="info-value">{{user.companyName}}</div>
        <div class="info-title">{{$t("user.responsible")}}</div>
        <div class="info-value">{{responsibleName}}</div>
        <div class="bottom-border"></div>
      </div>
      <div class="grid-data">
        <div class="info-title">{{$t("user.role")}}</div>
        <div class="info-value">{{$t(isClient() ? "roles.CLIENT" : "roles.ADMIN")}}</div>
        <div class="info-title" v-if="isClient()">{{$t("user.folder")}}</div>
        <div class="info-value" v-if="isClient()">{{user.folderName}}</div>
        <div class="info-title">{{$t("user.language")}}</div>
        <div class="info-value">{{$t(`languages.${user.locale}`)}}</div>
      </div>
    </div>
    <div class="block-content" v-if="isClient()">
      <div class="sub-header">
        <div class="page-title-layout">
          <p>{{$t("user.client_files")}}</p>
        </div>
        <button class="solid-button" type="button" v-on:click="addFile(File.Origins.mine)" :disabled="uploading">
          <i class="fas fa-plus" v-if="!uploading"></i>
          <span v-if="!uploading">{{$t("user.client_files_add")}}</span>
          <spinner :size="24" v-if="uploading"></spinner>
        </button>
      </div>
      <file-list
        v-if="userFiles.length > 0"
        :client-id="user.id"
        :show-file-status="false"
        :can-download="true"
        :can-rename="true"
        :can-delete="true"
        :files="userFiles"
        @refresh="loadFiles(File.Origins.mine)">
      </file-list>
      <input
        ref="userUpload"
        type="file"
        multiple
        @change="onFilesChanged($event, File.Origins.mine)"
        class="input-file">
    </div>
    <div class="block-content" v-if="isClient()">
      <div class="sub-header">
        <div class="page-title-layout">
          <p>{{$t("user.client_files_shared")}}</p>
        </div>
        <button class="solid-button" type="button" v-on:click="addFile(File.Origins.shared)" :disabled="uploading">
          <i class="fas fa-plus" v-if="!uploading"></i>
          <span v-if="!uploading">{{$t("user.client_files_add")}}</span>
          <spinner :size="24" v-if="uploading"></spinner>
        </button>
      </div>
      <file-list
        v-if="sharedFiles.length > 0"
        :client-id="user.id"
        :show-file-status="false"
        :can-download="true"
        :can-rename="true"
        :can-delete="true"
        :files="sharedFiles"
        @refresh="loadFiles(File.Origins.shared)">
      </file-list>
      <input
        ref="sharedUpload"
        type="file"
        multiple
        @change="onFilesChanged($event, File.Origins.shared)"
        class="input-file">
    </div>
  </div>
</template>

<script>
  import File from "../models/File.js";
  import Admin from "../models/Admin.js"
  import Client from "../models/Client.js"
  import spinner from "../components/spinner.vue";
  import fileList from "../components/fileList.vue"
  import userService from "../services/userService.js"
  import fileService from "../services/fileService.js"
  import Utils from "../services/utils.js";

  const PageModes = {
    CLIENT: 'client',
    ADMIN: 'admin'
  };

  export default {
    props: {
      pageMode: {
        type: String,
        default: function () {
          return PageModes.CLIENT
        }
      },
    },
    components: {
      fileList,
      spinner
    },
    data() {
      return {
        File: File,
        Utils: Utils,
        userFiles: [],
        sharedFiles: [],
        user: this.isClient() ? new Client() : new Admin(),
        responsibleName: '',
        uploading: false
      };
    },
    watch: {
      '$route'(to) {
        if (to.params.pageMode !== this.pageMode) {
          this.loadUser();
        }
      }
    },
    methods: {
      isClient() {
        return this.pageMode === PageModes.CLIENT;
      },
      loadClientAdmin() {
        if (this.isClient() && this.user.responsibleId) {
          userService.getAdmin(this.user.responsibleId).then(admin => {
            this.responsibleName = admin.displayName;
          })
        }
      },
      editUser(userId) {
        const routeName = this.isClient() ? 'clientEdit' : 'adminEdit';
        this.$router.push({name: routeName, params: {id: userId}})
      },
      loadUser() {
        const userId = this.$route.params.id;
        if (userId > 0) {
          const call = this.isClient() ? userService.getClient : userService.getAdmin;
          call(userId).then(user => {
            this.user = user;
            this.loadClientAdmin();
            this.loadFiles(File.Origins.mine);
            this.loadFiles(File.Origins.shared);
          });
        }
      },
      loadFiles(origin) {
        if (origin === File.Origins.mine) {
          this.userFiles = [];
        } else {
          this.sharedFiles = [];
        }
        if (this.user.id && this.isClient()) {
          fileService.getFiles(origin, this.user.id).then(files => {
            if (origin === File.Origins.mine) {
              this.userFiles = files;
            } else {
              this.sharedFiles = files;
            }
          });
        }
      },
      addFile(origin) {
        if (origin === File.Origins.mine) {
          this.$refs.userUpload.click(); // click on input
        } else {
          this.$refs.sharedUpload.click(); // click on input
        }
      },
      uploadFiles(files, origin) {
        let tooBig = false;
        let totalSize = 0;
        for (let x in files) {
          totalSize += ((files[x].size / 1024) / 1024); // MB
          if (totalSize > 200) {
            tooBig = true;
          }
        }
        if (tooBig) {
          this.$store.dispatch('alert/error', {message: this.$t("messages.file_size_limit")});
          return;
        }
        const fileCount = files.length;
        fileService.upload(files, this.user.id, origin)
          .then(() => {
            this.$store.dispatch('alert/success', this.$t("alerts.upload_file_success", {count: fileCount}));
            this.loadFiles(origin); // refresh
            this.uploading = false;
          })
          .catch(() => {
            this.$store.dispatch('alert/error', {
              message: this.$t("alerts.upload_file_error", {count: fileCount}),
              error: error
            });
            this.uploading = false;
          });
        this.uploading = true;
      },
      onFilesChanged(event, origin) {
        const files = event.target.files;
        this.uploadFiles(files, origin);
      }
    },
    mounted() {
      this.uploading = false;
      this.loadUser();
    }
  }
</script>

<style lang="scss" scoped>
  @import '../style/profil.scss';

  #page-layout {
    display: grid;
    grid-template-columns: 20% [content] auto 20%;
  }

  #page-header {
    @extend %common-header;
    justify-content: space-between;
  }

  .sub-header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
  }

  .input-file {
    opacity: 0; /* hide actual button */
    width: 0;
    height: 0;
    display: none;
    position: absolute;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/pages/manageUser.vue